import { useRef, useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TablePagination,
  TableSortLabel,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import SyncIcon from "@mui/icons-material/Sync";

import Tooltip from "@mui/material/Tooltip";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DescriptionIcon from "@mui/icons-material/Description";
import ArticleIcon from "@mui/icons-material/Article";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel } from "@fortawesome/free-solid-svg-icons";
import SearchIcon from "@mui/icons-material/Search";

import FilterModal from "./filter";
import UploadModal from "./uploadfile";
import StatusBadge from "./statusbadge";
import MetadataKB from "./metadata";

import Greenbanner from "@/components/header/greenbanner";

interface CustomPaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => void;
}

function CustomPaginationActions({
  count,
  page,
  rowsPerPage,
  onPageChange,
}: CustomPaginationActionsProps) {
  const [inputPage, setInputPage] = useState(page + 1);

  useEffect(() => {
    setInputPage(page + 1);
  }, [page]);

  const handlePageInputChange = (event: any) => {
    setInputPage(event.target.value);
  };

  const handlePageInputBlur = () => {
    let newPage = parseInt(inputPage, 10) - 1;
    if (isNaN(newPage) || newPage < 0) newPage = 0;
    if (newPage >= Math.ceil(count / rowsPerPage))
      newPage = Math.ceil(count / rowsPerPage) - 1;
    onPageChange(null, newPage);
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
      {/* Back Button */}
      <IconButton
        onClick={(event) => onPageChange(event, page - 1)}
        disabled={page === 0}
        sx={{ color: "black" }}
      >
        <KeyboardArrowLeft />
      </IconButton>

      {/* Editable Page Number Input */}
      <TextField
        value={inputPage}
        onChange={handlePageInputChange}
        onBlur={handlePageInputBlur}
        onKeyPress={(event) => {
          if (event.key === "Enter") handlePageInputBlur();
        }}
        sx={{
          width: "50px",
          textAlign: "center",
          "& input": { textAlign: "center", padding: "6px 0" },
        }}
        size="small"
      />

      {/* Next Button */}
      <IconButton
        onClick={(event) => onPageChange(event, page + 1)}
        disabled={(page + 1) * rowsPerPage >= count}
        sx={{ color: "black" }}
      >
        <KeyboardArrowRight />
      </IconButton>
    </Box>
  );
}

export default function Knowledgebase() {
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const storedUserInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
  const storedUserData = JSON.parse(localStorage.getItem("user_data") || "{}");
  const userRoles = storedUserData.roles || [];
  const isAdmin = userRoles.includes("admin");

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const today = dayjs();
  
  // CPLM Sync state
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState<string | null>(null);

  const handleStartDateChange = (date: any) => {
    setStartDate(date);
  };
  const [kbDocumentData, setKbDocumentData] = useState(null);
  const [firstEdit, setFirstEdit] = useState(false);

  const handleEndDateChange = (date: any) => {
    setEndDate(date);
  };
  const setClearDates = () => {
    setStartDate(null);
    setEndDate(null);
  };

  const [tableData, setTableData] = useState([]);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");

  const handleSort = (property: any) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);

    const sortedData = [...tableData].sort((a, b) => {
      if (a[property] < b[property]) return isAsc ? 1 : -1;
      if (a[property] > b[property]) return isAsc ? -1 : 1;
      return 0;
    });

    setTableData(sortedData);
  };

  const [isMetadataOpen, setIsMetadataOpen] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);

  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);

  const getFileIcon = (docUrl: any) => {
    if (!docUrl) return <InsertDriveFileIcon sx={{ fontSize: 18 }} />;

    const extension = docUrl.split(".").pop().toLowerCase();
    switch (extension) {
      case "pdf":
        return <PictureAsPdfIcon sx={{ fontSize: 18, color: "red" }} />;
      case "png":
      case "jpg":
      case "jpeg":
        return <ImageIcon sx={{ fontSize: 18, color: "blue" }} />;
      case "txt":
        return <DescriptionIcon sx={{ fontSize: 18, color: "green" }} />;
      case "xls":
      case "xlsx":
      case "csv":
        return (
          <FontAwesomeIcon
            icon={faFileExcel}
            style={{ fontSize: 18, color: "green" }}
          />
        );
      case "doc":
      case "docx":
        return (
          <ArticleIcon
            sx={{ fontSize: 18, color: "blue", marginRight: "10px" }}
          />
        );
      default:
        return <InsertDriveFileIcon sx={{ fontSize: 18 }} />;
    }
  };

  const handleRowClick = (row: any) => {
    setSelectedRow(row);
    setSelectedRowId(row.id);
  };

  const handleMetadataClick = () => {
    if (selectedRow) {
      setIsMetadataOpen(true);
    }
  };

  const tooltipMessage = selectedRow
    ? `Edit for ${selectedRow.processing_status} status is ${
        selectedRow.processing_status === "Completed" ? "enabled" : "disabled"
      }`
    : "";

  const handleMetadataSubmit = async (data: any) => {
    try {
      if (firstEdit) {
        const userEditedData = {
          ...(kbDocumentData as any),
        };
        setKbDocumentData(userEditedData);
        saveKbDocument(data);
      } else {
        const accessToken = localStorage.getItem("accessToken");
        const userInfo = JSON.parse(localStorage.getItem("user_data") ?? "");
        const uuid = userInfo.id;
        const payload = {
          ...data,
          document_id: selectedRowId,
          doc_name_lowercase: data?.doc_name,
          user_id: uuid,
          keywords: data?.keywords || [],
        };
        const response = await axios.put(
          `${import.meta.env.VITE_API_URL}/kb_documents/${selectedRowId}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (response.status === 200) {
          console.log("Metadata updated successfully:", response.data);

          setSelectedRow((prev: any) => ({ ...prev, ...data }));
          setTableData(
            (prevTableData) =>
              prevTableData.map((row: any) =>
                row.id === selectedRowId ? { ...row, ...data } : row
              ) as any
          );

          setIsMetadataOpen(false);
          toast.success("Metadata updated successfully!", {
            position: "bottom-left",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          });
          await fetchDocuments();
        } else {
          console.error("Metadata update failed:", response.status);
        }
      }
    } catch (error) {
      console.error("Metadata update error:", error);
    }
  };

  const fileInputRef = useRef(null);

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const handleOpenFilterModal = () => setIsFilterModalOpen(true);
  const handleCloseFilterModal = () => setIsFilterModalOpen(false);
  const [filters, setFilters] = useState({
    cplmStatus: [],
    product: [],
    designStep: [],
    documentType: [],
    documentVersion: [],
  });

  const [uploadProgress, setUploadProgress] = useState(0);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);

  const handleChange = (field: any) => (event: any) => {
    setFilters({
      ...filters,
      [field]: event.target.value,
    });
  };

  const handleApplyFilters = () => {
    console.log("Applied Filters:", filters);
    fetchDocuments();
    handleCloseFilterModal();
  };

  const handleResetFilters = () => {
    setFilters({
      cplmStatus: "",
      product: "",
      designStep: "",
      documentType: "",
      documentVersion: "",
    } as any);
  };

  const totalFiltersApplied = Object.values(filters).filter(
    (value) => value && value.length > 0
  ).length;

  const handleFileUploadClick = () => {
    fileInputRef?.current?.click();
  };

  const extractMetadataFromFile = (file: any) => {
    let designStep = "";

    const objects = file.split("/");
    let docName = objects[objects.length - 1].split(".")[0];
    const spaceSep = docName.split(" ");
    let docId = spaceSep[0];

    if (!docId.includes("-")) {
      const pattern = /[A-Za-z]+-\d+(?=\s|\.|$)/;
      const match = docName.match(pattern);
      if (match) {
        docId = match[0];
      } else {
        docId = docName;
      }
    }

    const docType = docId.split("-")[0];

    const docExtension = file.split(".")[objects.length - 1].toLowerCase();
    const extensionProcessMap = {
      pdf: "pdf-docling",
      docx: "docx-docling",
      xlsx: "",
    };
    let extractionProcess = extensionProcessMap[docExtension] || "";

    const pattern = /Rev[-\s]*([0-9]+)/;
    const match = docName.match(pattern);
    let docVersion = null;
    if (match) {
      docVersion = parseInt(match[1]);
    }

    const now = new Date();

    const metadata = {
      doc_location: file,
      created_on: now,
      updated_on: now,
      is_deleted: false,
      doc_id: docId,
      doc_name: docName,
      design_step: designStep,
      doc_version: docVersion,
      doc_type: docType,
      upstream_doc: "",
      downstream_doc: "",
      doc_owner: "",
      product: "",
      relationship_type: "",
      language: "ENGLISH",
      extraction_process: extractionProcess,
      tenant_id: "DEXCOM COPILOT",
    };

    setSelectedRow(metadata as any);

    return metadata;
  };

  const handleFileChange = async (event: any) => {
    if (event && typeof event.preventDefault === "function") {
      event.preventDefault();
    }

    const selectedFile = event.target?.files?.[0];
    if (!selectedFile) return;

    const formData = new FormData();
    formData.append("files", selectedFile);
    const metadata = extractMetadataFromFile(selectedFile.name);
    try {
      const accessToken = localStorage.getItem("accessToken");
      const userInfo = JSON.parse(localStorage.getItem("user_data") ?? "");
      const uuid = userInfo.id;
      const userEmail = userInfo.email;
      formData.append("project_name", "test");
      formData.append("user_id", uuid);
      formData.append("doc_type", metadata.doc_type || "-");
      formData.append("file_types", selectedFile?.type || "pdf");
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/kb-documents-upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent: any) => {
            const progress = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100
            );
            setUploadProgress(progress);
          },
        }
      );
      if (response.status === 200) {
        const data = await response.data;
        const docUrl = data.data[0].doc_url;
        const documentMetaData = {
          user_id: uuid,
          type: metadata.doc_type,
          doc_name: metadata.doc_name,
          doc_name_lowercase: metadata.doc_name?.toString()?.toLowerCase(),
          doc_version: metadata.doc_version,
          doc_url: docUrl,
          conversations_id: "",
          feedback: 0,
          project_name: "test",
          processing_status: "Inprogress",
          owner: userEmail,
          design_step: "Design Input",
          upstream: "upstream",
          downstream: "downstream",
          product: metadata.product,
          doc_id: metadata.doc_id,
          doc_owner: "doc_owner",
          tenant_id: "Dexcom Copilot",
          review_status: "Reviewed",
          relationship_type: "-",
        };

        setKbDocumentData(documentMetaData as any);
      } else {
        console.error("Upload failed:", response.status);
      }
    } catch (error) {
      console.error("Upload error:", error);
    }
  };

  const downloadDocument = async (gcsPath: string) => {
    try {
      const accessToken = localStorage.getItem("accessToken");
      const data = {
        gcs_path: gcsPath || "",
      };
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/download-gcs-file?gcs_path=${
          data?.gcs_path
        }`,
        data,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          onDownloadProgress: (progressEvent) => {
            const progress = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100
            );
          },
          responseType: "blob",
        }
      );
      if (response.status === 200) {
        const blob = await response.data;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", data?.gcs_path.split("/").pop());
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        console.error("Download failed:", response.status);
      }
    } catch (error) {}
  };

  const saveKbDocument = async (data: any) => {
    try {
      const accessToken = localStorage.getItem("accessToken");
      const payload = {
        ...(kbDocumentData as any),
        doc_name: data?.doc_name || "",
        doc_name_lowercase: data?.doc_name?.toString()?.toLowerCase() || "",
        doc_type: data?.doc_type || "",
        upstream: data?.upstream || "",
        downstream: data?.downstream || "",
        keywords: data?.keywords || [],
        description: data?.description || "",
        cplm_status: data?.cplm_status || "Unknown",
        doc_version: (parseFloat(data?.doc_version) || 0).toString() || "",
        design_step: data?.design_step || "",
        reviewer_id: data?.reviewer_id || "akaskh.mhatare@dexcom.com",
        review_status: data?.review_status || "",
        doc_owner: data?.doc_owner || "",
        relationship_type: data?.relationship_type || "",
        product: data?.product || "G7",
        tenant_id: "Dexcom Copilot",
        uploaded_by: kbDocumentData?.owner,
      };
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/kb_documents`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (response.status === 200) {
        console.log("KB Document saved successfully:", response.data);
        setTableData((prevData) => [...prevData, kbDocumentData] as any);
        setIsUploadOpen(false);
        setUploadProgress(0);
        toast.success("File uploaded successfully! ", {
          position: "bottom-left",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
        setIsUploadOpen(false);
        await new Promise((resolve) => setTimeout(resolve, 1000));
        await fetchDocuments();
      } else {
        console.error("KB Document save failed:", response.status);
      }

      setFirstEdit(false);
    } catch (error) {
      console.error("KB Document save error:", error);
    }
  };

  const confirmUpload = () => {
    setIsUploadOpen(false);
    setFirstEdit(true);
    setIsMetadataOpen(true);
  };

  const [currentPage, setCurrentPage] = useState(0);
  const [totalDocuments, setTotalDocuments] = useState(0);

  const fetchDocuments = async () => {
    try {
      const accessToken = localStorage.getItem("accessToken");
      console.log("Fetching documents for page:", currentPage);

      const formattedStartDate = startDate
        ? startDate.format("YYYY-MM-DD")
        : "";
      const formattedEndDate = endDate ? endDate.format("YYYY-MM-DD") : "";

      const queryParams = new URLSearchParams({
        page: currentPage,
        limit: 10,
        ...(searchTerm && { doc_name_lowercase: searchTerm?.toLowerCase() }),
        ...(formattedStartDate && { start_date: formattedStartDate }),
        ...(formattedEndDate && { end_date: formattedEndDate }),
        ...(filters.product?.length > 0 && {
          product: filters.product.join(","),
        }),
        ...(filters.designStep?.length > 0 && {
          design_step: filters.designStep.join(","),
        }),
        ...(filters.documentType?.length > 0 && {
          doc_type: filters.documentType.join(","),
        }),
        ...(filters.documentVersion?.length > 0 && {
          doc_version: filters.documentVersion
            ?.map((v) => v.toString())
            .toString(),
        }),
        ...(filters.cplmStatus?.length > 0 && {
          cplm_status: filters.cplmStatus.join(","),
        }),
      });

      const response = await axios.get(
        `${
          import.meta.env.VITE_API_URL
        }/kb_documents?${queryParams.toString()}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      if (response.status === 200) {
        setTableData(response.data.data.Documents);
        setTotalDocuments(response.data.data.total_docs);
      } else {
        console.error("Fetch documents failed:", response.status);
      }
    } catch (error: any) {
      console.error("Fetch documents error:", error);
      console.error(
        "Fetch documents error:",
        error.response?.data || error.message
      );
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [searchTerm, currentPage, startDate, endDate, filters]);

  const handlePageChange = (_, newPage: any) => {
    setCurrentPage(newPage);
  };

  const handleSearchChange = (event: any) => {
    setSearchTerm(event.target.value);
    setCurrentPage(0);
  };

  const handleDeleteDocument = async (documentId: any) => {
    setDocumentToDelete(documentId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const accessToken = localStorage.getItem("accessToken");
      const response = await axios.delete(
        `${import.meta.env.VITE_API_URL}/kb_documents/${documentToDelete}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      if (response.status === 200) {
        console.log("Document deleted successfully:", response.data);
        fetchDocuments();
        setDeleteDialogOpen(false);
      } else {
        console.error("Delete document failed:", response.status);
      }
    } catch (error) {
      console.error("Delete document error:", error);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
  };

  // CPLM Sync Functions
  const handleSyncCPLM = async () => {
    try {
      setIsSyncing(true);
      setSyncStatus(null);
      const accessToken = localStorage.getItem("accessToken");
      const userInfo = JSON.parse(localStorage.getItem("user_data") ?? "{}");
      const email = userInfo.email;

      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/cplm/trigger?email=${encodeURIComponent(email)}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.status === 200 && response.data.success) {
        const result = response.data.data;
        
        if (result.triggered) {
          setSyncStatus("success");
          toast.success("CPLM sync started successfully! Documents will be processed in the background.", {
            position: "bottom-left",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "light",
          });
          
          // Optionally refresh document list after a delay
          setTimeout(() => {
            fetchDocuments();
          }, 3000);
        } else if (result.reason === "no_files") {
          setSyncStatus("info");
          toast.info("No files found in buffer to process.", {
            position: "bottom-left",
            autoClose: 3000,
            theme: "light",
          });
        } else {
          setSyncStatus("info");
          toast.info(response.data.message || "Sync is already in progress or recently completed.", {
            position: "bottom-left",
            autoClose: 3000,
            theme: "light",
          });
        }
      } else {
        setSyncStatus("error");
        toast.error(response.data.message || "Failed to sync CPLM documents", {
          position: "bottom-left",
          autoClose: 3000,
          theme: "light",
        });
      }
    } catch (error: any) {
      console.error("CPLM sync error:", error);
      setSyncStatus("error");
      
      let errorMessage = "Failed to sync CPLM documents";
      if (error.response?.status === 401 || error.response?.status === 403) {
        errorMessage = "You don't have permission to sync CPLM documents";
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      toast.error(errorMessage, {
        position: "bottom-left",
        autoClose: 3000,
        theme: "light",
      });
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <div>
      {/* Green Banner */}
      <Greenbanner name={`Welcome ${storedUserInfo.name}`} />
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
        }}
      >
        {/* Back Button */}
        <Box
          sx={{
            flex: "0 1 auto",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minWidth: "8rem",
            margin: "0 3.5rem 0 2rem",
          }}
        >
          <Button
            variant="contained"
            sx={{
              height: "2.5rem",
              width: "6rem",
              backgroundColor: "#137d00",
              ":hover": { backgroundColor: "#0e5c00" },
            }}
            onClick={() => navigate(-1)}
          >
            <Typography
              variant="button"
              component="div"
              sx={{ textTransform: "capitalize" }}
            >
              Back
            </Typography>
          </Button>
        </Box>

        {/* Header */}
        <Box
          sx={{
            flex: "1",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            backgroundColor: "#f5f5f5",
            padding: "1rem",
            minWidth: 0,
            margin: "0.5rem 3rem 0 0",
            borderRadius: "4px",
          }}
        >
          <Typography
            sx={{
              fontSize: "1.2rem",
              fontWeight: "bold",
              paddingLeft: "1rem",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "flex",
              alignItems: "center",
            }}
          >
            Manage Files
          </Typography>
        </Box>
      </Box>
      {/* New Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          padding: "1rem 3rem",
          flexWrap: "wrap",
          gap: "1rem",
          marginTop: "0.15rem",
        }}
      >
        {/* Buttons Container */}
        <Box
          sx={{
            display: "flex",
            gap: "1.5rem",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="contained"
            sx={{
              height: "2.5rem",
              width: "9rem",
              backgroundColor: "var(--colors-brand-secondary)",
              ":hover": { backgroundColor: "#0e5c00" },
            }}
            onClick={() => setIsUploadOpen(true)}
          >
            Upload File
          </Button>
          
          {/* CPLM Sync Button - Only visible to admins */}
          {isAdmin && (
            <Button
              variant="contained"
              sx={{
                height: "2.5rem",
                width: "9rem",
                backgroundColor: isSyncing ? "#888" : "#1976d2",
                ":hover": { backgroundColor: isSyncing ? "#888" : "#1565c0" },
              }}
              onClick={handleSyncCPLM}
              disabled={isSyncing}
              startIcon={isSyncing ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
            >
              {isSyncing ? "Syncing..." : "Sync CPLM"}
            </Button>
          )}
          
          <Tooltip
            title={tooltipMessage}
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: "#000",
                  color: "#fff",
                  fontSize: "0.875rem",
                  borderRadius: "4px",
                  padding: "12px",
                },
              },
            }}
          >
            <span>
              <Button
                variant="contained"
                sx={{
                  height: "2.5rem",
                  width: "9rem",
                  backgroundColor: "var(--colors-brand-secondary)",
                  ":hover": { backgroundColor: "#0e5c00" },
                }}
                onClick={handleMetadataClick}
                disabled={
                  !selectedRow ||
                  ["Inprogress", "Failed"].includes(
                    selectedRow?.processing_status
                  )
                }
              >
                Edit Meta Data
              </Button>
            </span>
          </Tooltip>
          {(selectedRow?.processing_status === "Failed" ||
            selectedRow?.processing_status === "Inprogress") && (
            <div className="text-red-600">Edit disabled</div>
          )}
        </Box>

        {/* Search Bar Container */}
        <Box
          sx={{
            flex: "1",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minWidth: "200px",
          }}
        >
          <TextField
            fullWidth
            placeholder="Search for existing documents....."
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#888" }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: "4px",
              },
            }}
            sx={{
              backgroundColor: "#fff",
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#ccc",
                },
                "&:hover fieldset": {
                  borderColor: "#888",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#137d00",
                },
              },
            }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: "1rem",
            alignItems: "center",
            flexShrink: 0,
            marginTop: "-0.5rem",
          }}
        >
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            sx={{ paddingTop: "1rem" }}
          >
            <DemoContainer components={["DatePicker"]}>
              <DatePicker
                format="MM-DD-YYYY"
                label="Start Date"
                value={startDate}
                onChange={handleStartDateChange}
                maxDate={today}
                slotProps={{
                  field: {
                    clearable: true,

                    onClear: () => handleStartDateChange(null),
                    inputProps: {
                      readOnly: true,
                    },
                  },
                  popper: {
                    sx: {
                      ".MuiPickersDay-root, .MuiPickersCalendarHeader-root, .MuiDayPicker-weekDayLabel, .MuiTypography-caption":
                        {
                          fontSize: "15px",
                        },
                    },
                  },
                }}
              />
            </DemoContainer>
          </LocalizationProvider>
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            sx={{ paddingTop: "1rem" }}
          >
            <DemoContainer components={["DatePicker"]}>
              <DatePicker
                format="MM-DD-YYYY"
                label="End Date"
                value={endDate}
                onChange={handleEndDateChange}
                minDate={startDate}
                maxDate={today}
                slotProps={{
                  field: {
                    clearable: true,

                    onClear: () => handleEndDateChange(null),
                    inputProps: {
                      readOnly: true,
                    },
                  },
                  popper: {
                    sx: {
                      ".MuiPickersDay-root, .MuiPickersCalendarHeader-root, .MuiDayPicker-weekDayLabel, .MuiTypography-caption":
                        {
                          fontSize: "15px",
                        },
                    },
                  },
                }}
              />
            </DemoContainer>
          </LocalizationProvider>
        </Box>

        {/* Filter Button */}
        <Button
          variant="outlined"
          sx={{
            position: "relative",
            height: "2.5rem",
            minWidth: "8rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "1.6rem",
            marginLeft: "auto",
            flexShrink: 0,
            overflow: "visible",
          }}
          onClick={handleOpenFilterModal}
        >
          <TuneIcon sx={{ marginRight: "1rem" }} />
          <Box sx={{ flexGrow: 1, textAlign: "left" }}>Filter</Box>
          {totalFiltersApplied > 0 && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                right: "5%",
                transform: "translateY(-50%)",
                backgroundColor: "green",
                color: "white",
                fontSize: "0.75rem",
                width: "1.5rem",
                height: "1.5rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "50%",
                fontWeight: "bold",
                border: "2px solid white",
              }}
            >
              {totalFiltersApplied}
            </Box>
          )}
        </Button>
      </Box>

      {/* Table */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#d9f7be" }}>
                {[
                  { label: "Document Name", key: "doc_name" },
                  { label: "ID", key: "doc_id" },
                  { label: "Version", key: "doc_version" },
                  { label: "Doc Type", key: "type" },
                  { label: "Design Step", key: "design_step" },
                  { label: "Shared On", key: "created_time" },
                  { label: "Document Owner Name", key: "owner" },
                  { label: "CPLM (Cloud PLM)", key: "cplm_status" },
                  { label: "Processing Status", key: "processing_status" },
                  { label: "Action", key: null },
                ].map((header, index) => (
                  <TableCell
                    key={index}
                    sx={{
                      fontWeight: "bold",
                      fontSize: "0.85rem",
                      borderBottom: "none",
                      whiteSpace: "nowrap",
                      cursor: header.key ? "pointer" : "default",
                    }}
                  >
                    {" "}
                    {header.key ? (
                      <TableSortLabel
                        active={orderBy === header.key}
                        direction={orderBy === header.key ? order : "asc"}
                        onClick={() => handleSort(header.key)}
                      >
                        {header.label}
                      </TableSortLabel>
                    ) : (
                      header.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {/* {tableData.map((row, rowIndex) => ( */}
              {tableData.map((row: any) => (
                <TableRow
                  key={row.id}
                  onClick={() => handleRowClick(row)}
                  sx={{
                    backgroundColor:
                      selectedRowId === row.id ? "#e6f7ff" : "inherit",
                    cursor: "pointer",
                  }}
                >
                  <TableCell sx={{ borderBottom: "none", fontSize: "0.9rem" }}>
                    <Box display="flex" alignItems="center">
                      {getFileIcon(row.doc_url)}
                      <span style={{ marginLeft: "0.75rem" }}>
                        {row.doc_name}
                      </span>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none", fontSize: "0.9rem" }}>
                    {row.doc_id || row.id}
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none", fontSize: "0.9rem" }}>
                    {row.doc_version}
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none", fontSize: "0.9rem" }}>
                    {row.doc_type || row.type}
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none", fontSize: "0.9rem" }}>
                    {row.design_step}
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none", fontSize: "0.9rem" }}>
                    {dayjs(row.created_time).format("MM-DD-YYYY")}
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none", fontSize: "0.9rem" }}>
                    {row.owner || row.uploaded_by}
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none", fontSize: "0.9rem" }}>
                    <Tooltip
                      title={`CPLM (Cloud PLM) status is "${row.cplm_status}"`}
                      componentsProps={{
                        tooltip: {
                          sx: {
                            backgroundColor: "#000",
                            color: "#fff",
                            fontSize: "0.875rem",
                            borderRadius: "4px",
                            padding: "8px",
                          },
                        },
                      }}
                    >
                      <span>
                        <StatusBadge status={row.cplm_status} type="CPLM" />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none", fontSize: "0.9rem" }}>
                    <Tooltip
                      title={`Processing status is "${row.processing_status}"`}
                      componentsProps={{
                        tooltip: {
                          sx: {
                            backgroundColor: "#000",
                            color: "#fff",
                            fontSize: "0.875rem",
                            borderRadius: "4px",
                            padding: "8px",
                          },
                        },
                      }}
                    >
                      <span>
                        <StatusBadge
                          status={row.processing_status}
                          type="CPLM"
                        />
                      </span>
                    </Tooltip>
                  </TableCell>

                  <TableCell
                    sx={{
                      borderBottom: "none",
                      fontSize: "0.9rem",
                      color: "#137d00",
                      cursor: "pointer",
                    }}
                  >
                    <Box display="flex" alignItems="center">
                      <Tooltip
                        title="Download"
                        componentsProps={{
                          tooltip: {
                            sx: {
                              backgroundColor: "#000",
                              color: "#fff",
                              fontSize: "0.875rem",
                              borderRadius: "4px",
                              padding: "8px",
                            },
                          },
                        }}
                      >
                        <IconButton
                          aria-label="Download"
                          sx={{ color: "inherit" }}
                          onClick={() => downloadDocument(row.doc_url)}
                        >
                          <SaveAltIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title="Delete"
                        componentsProps={{
                          tooltip: {
                            sx: {
                              backgroundColor: "#000",
                              color: "#fff",
                              fontSize: "0.875rem",
                              borderRadius: "4px",
                              padding: "8px",
                            },
                          },
                        }}
                      >
                        <IconButton
                          aria-label="Delete"
                          sx={{ color: "inherit" }}
                          onClick={() => handleDeleteDocument(row.id)}
                        >
                          <DeleteIcon sx={{ color: "red" }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Table Pagination */}
        <Box
          sx={{
            borderTop: "1px solid #ddd",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <TablePagination
            component="div"
            count={totalDocuments || 0}
            rowsPerPage={rowsPerPage}
            page={currentPage}
            onPageChange={handlePageChange}
            rowsPerPageOptions={[]}
            ActionsComponent={CustomPaginationActions}
            labelDisplayedRows={({ from, to, count }) => {
              const total = totalDocuments || 0;
              const start = total === 0 ? 0 : currentPage * rowsPerPage + 1;
              const end = Math.min(total, start + rowsPerPage - 1);
              return `${start}-${end} of ${total}`;
            }}
          />
        </Box>
      </Box>
      <UploadModal
        open={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        handleFileUploadClick={handleFileUploadClick}
        fileInputRef={fileInputRef}
        handleFileChange={handleFileChange}
        uploadProgress={uploadProgress}
        confirmUpload={confirmUpload}
      />
      <MetadataKB
        open={isMetadataOpen}
        onClose={() => setIsMetadataOpen(false)}
        data={selectedRow}
        handleMetadataSubmit={handleMetadataSubmit}
        firstEdit={firstEdit}
      />
      <FilterModal
        open={isFilterModalOpen}
        handleClose={handleCloseFilterModal}
        filters={filters}
        setFilters={setFilters}
        handleApplyFilters={handleApplyFilters}
        handleResetFilters={handleResetFilters}
      />
      <ToastContainer />
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Are you sure you want to delete this document?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleCancelDelete}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConfirmDelete} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
